import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, AlertCircle, ArrowRight } from 'lucide-react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { useActivityLog } from '../../../contexts/ActivityLogContext';
import { toast } from 'react-hot-toast';
import logo3D from '../../../assets/logos/3D-LOGO.png';
import logoUDSM from '../../../assets/logos/UDSM-LOGO.png';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useApiAuth();
  const { logUserSessionActivity } = useActivityLog();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  
  // Handle navigation state
  useEffect(() => {
    // Only show "Please sign in" message if:
    // 1. We have a 'from' location (meaning we were redirected here)
    // 2. It's not from a logout action
    // 3. The 'from' location is not the login page itself
    if (location.state?.from && 
        !location.state?.isLogout && 
        location.state.from !== '/login' &&
        location.state.from !== '/logout') {
      toast.error('Please sign in to access this page');
    }
  }, [location.state]);

  const handleOkayClick = () => {
    setShowApprovalDialog(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      await login(email, password);
      
      // Log user login activity
      await logUserSessionActivity('login');
      
      // Navigate to the page user tried to access, or home if none
      const destination = location.state?.from || '/';
      navigate(destination, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific error cases
      if (error.detail && error.detail.includes('pending approval')) {
        setShowApprovalDialog(true);
      } else if (error.detail && error.detail.includes('suspended')) {
        toast.error('Your account has been suspended');
      } else {
        // Error toast is already shown by authService
      }
    } finally {
      setLoading(false);
    }
  };

  // Approval Pending Dialog
  const ApprovalDialog = ({ onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative w-full max-w-md p-6 m-4 border shadow-2xl rounded-2xl" style={{ backgroundColor: NEUTRAL_COLORS.white, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
            <AlertCircle className="w-6 h-6" style={{ color: BRAND_COLORS.accent }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Approval Required</h3>
        </div>

        <p className="mb-6" style={{ color: SYSTEM_COLORS.textMuted }}>
          Your account is pending administrator approval. You'll be notified once your account has been approved.
        </p>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white rounded-lg"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            Okay, I'll wait
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: NEUTRAL_COLORS.white }}>
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_1fr]">
        <div className="hidden p-10 lg:flex xl:p-12" style={{ backgroundColor: BRAND_COLORS.primary }}>
          <div className="flex flex-col w-full min-h-full">
            <div className="flex items-center gap-4">
              <img src={logo3D} alt="3D Lab logo" className="h-12 w-12 rounded-lg bg-white p-1.5" />
              <img src={logoUDSM} alt="UDSM logo" className="h-12 w-12 rounded-lg bg-white p-1.5" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: SYSTEM_COLORS.textOnBrandMedium }}>
                A.M.A.R.M.S
              </span>
            </div>

            <div className="max-w-md my-auto">
              <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: SYSTEM_COLORS.textOnBrandSoft }}>
                Operations Workspace
              </p>
              <h1 className="mt-4 text-4xl font-semibold leading-tight" style={{ color: NEUTRAL_COLORS.white }}>A.M.A.R.M.S Platform</h1>
              <p className="mt-4 text-base leading-7" style={{ color: SYSTEM_COLORS.textOnBrandStrong }}>
               Active Monitoring and Resource Management System
              </p>
            </div>

            <div className="pt-5 border-t" style={{ borderColor: SYSTEM_COLORS.borderOnBrand }}>
              <p className="text-sm font-medium" style={{ color: SYSTEM_COLORS.textOnBrandStrong }}>
                Developed and Managed by{' '}
                <a
                  href="https://robotics.udsm.ac.tz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold "
                  style={{ color: BRAND_COLORS.accent, textDecorationColor: SYSTEM_COLORS.accentTint }}
                >
                  3D Solutions and Robotics Lab - University of Dar es Salaam
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center px-4 py-8 sm:px-8 sm:py-10 lg:px-10">
          <div className="w-full max-w-lg mx-auto">
            <div className="flex justify-center gap-3 mb-6 lg:hidden">
              <img src={logo3D} alt="3D Lab logo" className="w-10 h-10 p-1 border rounded-lg" style={{ borderColor: SYSTEM_COLORS.border }} />
              <img src={logoUDSM} alt="UDSM logo" className="w-10 h-10 p-1 border rounded-lg" style={{ borderColor: SYSTEM_COLORS.border }} />
            </div>
            <p className="lg:hidden mb-6 text-center text-[11px] font-semibold uppercase tracking-[0.16em] sm:text-xs sm:tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
              A.M.A.R.M.S Platform
            </p>
            <p className="text-sm font-semibold leading-6 text-center lg:hidden mb-7" style={{ color: NEUTRAL_COLORS.black }}>
              Active Monitoring and Resource Management System
            </p>

            <div className="mb-7 sm:mb-8">
              <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>Welcome Back</p>
              <h2 className="mt-2 text-2xl font-bold sm:text-3xl" style={{ color: NEUTRAL_COLORS.black }}>Sign in to your account</h2>
              <p className="mt-2 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                Don't have an account?{' '}
                <Link to="/signup" className="font-semibold underline underline-offset-4" style={{ color: BRAND_COLORS.primary, textDecorationColor: SYSTEM_COLORS.primaryTint }}>
                  Create one
                </Link>
              </p>
            </div>

            <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Email address</label>
                <div className="relative">
                  <Mail className="absolute -translate-y-1/2 left-3 top-1/2" size={18} style={{ color: BRAND_COLORS.primary }} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full py-3 pl-10 pr-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                    style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                    placeholder="name@organization.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Password</label>
                <div className="relative">
                  <Lock className="absolute -translate-y-1/2 left-3 top-1/2" size={18} style={{ color: BRAND_COLORS.primary }} />
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full py-3 pl-10 pr-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                    style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                    placeholder="Enter your password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center w-full gap-2 px-4 py-3 text-sm font-semibold text-white transition rounded-xl disabled:opacity-60"
                style={{ backgroundColor: BRAND_COLORS.primary }}
              >
                {loading ? 'Signing in...' : 'Sign in'}
                {!loading && <ArrowRight size={16} />}
              </button>
            </form>

            <p className="mt-6 text-xs leading-5 text-center lg:hidden" style={{ color: SYSTEM_COLORS.textMuted }}>
              Developed and managed by{' '}
              <a
                href="https://robotics.udsm.ac.tz"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline underline-offset-4"
                style={{ color: BRAND_COLORS.accent, textDecorationColor: SYSTEM_COLORS.accentTint }}
              >
                3D Solutions and Robotics Lab - University of Dar es Salaam
              </a>
            </p>
          </div>
        </div>
      </div>

      {showApprovalDialog && <ApprovalDialog onClose={handleOkayClick} />}
    </div>
  );
};

export default Login;